/**
 * MqttSuspectedBanner — surfaced at the top of the TicketActivity modal
 * whenever MTTR thinks the MQTT broker / publish path is the root cause
 * (Tier 3 fresh, Tier 1 + 2 silent). The runner-side flag is
 * `box.publish_chain_broken`; this component only renders when it's true.
 *
 * When the runner could reach the edge box, we surface the box's actually-
 * configured BrokerAddress (green if it matches the canonical value, red
 * otherwise). When we couldn't reach the box, we show the
 * expected-vs-common-misconfig pair so the operator can run the wizard's
 * broker-check step.
 */
import React from "react";

import type { Box } from "../../types/boxes";

// Fallback when the snapshot is pre-072 and doesn't carry
// `expected_mqtt_broker` per box. Matches the historical hardcoded
// value so behavior degrades to the prior banner output.
const _DEFAULT_EXPECTED_BROKER = "hq-telemetry-testing.tailc36a4e.ts.net";

export const MqttSuspectedBanner: React.FC<{ box: Box }> = ({ box }) => {
  if (!box.publish_chain_broken) return null;
  const expected = box.expected_mqtt_broker || _DEFAULT_EXPECTED_BROKER;
  return (
        <div className="mb-3 rounded border border-warning-400 bg-warning-50 p-3 text-xs text-warning-900">
          <div className="font-semibold mb-1">⚠ MQTT is suspected as the root cause</div>
          <div className="text-warning-800">
            Box <code className="font-mono">{box.scu_name}</code> is producing
            telemetry locally (Tier 3 fresh) but it isn't reaching the central
            broker. Most common cause: wrong{" "}
            <code className="font-mono">MqttSettings.BrokerAddress</code> in
            the edge box's <code className="font-mono">app_config</code>.
          </div>
          {box.edge_mqtt_broker_address ? (
            <div className="mt-2 rounded border border-warning-300 bg-white/60 p-2">
              <div className="text-warning-900 font-semibold">
                Box is currently configured with:
              </div>
              <code
                className={
                  "block font-mono mt-0.5 text-[13px] " +
                  (box.edge_mqtt_broker_address === expected
                    ? "text-success-700"
                    : "text-error-700")
                }
              >
                {box.edge_mqtt_broker_address}
              </code>
              {box.edge_mqtt_broker_address !== expected && (
                <div className="text-warning-800 mt-1">
                  Expected:{" "}
                  <code className="font-mono">{expected}</code>
                </div>
              )}
            </div>
          ) : (
            <div className="text-warning-800 mt-1.5">
              Expected:{" "}
              <code className="font-mono">{expected}</code>
              <div className="italic text-warning-700 mt-1">
                (Box's actual broker value couldn't be read this cycle — use
                the wizard's broker-check step.)
              </div>
            </div>
          )}
          <div className="text-warning-700 mt-1.5 italic">
            The troubleshooting wizard below has a dedicated step for this fix.
          </div>
        </div>
  );
};

export default MqttSuspectedBanner;
